import { useState, useEffect } from 'react';
import api from '../services/api';
import PageHeader from '../components/ui/PageHeader';
import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';
import SystemSettingsForm from '../components/SystemSettingsForm';
import ConfirmationModal from '../components/ConfirmationModal';
import { useToast } from '../hooks/useToast';
import {
  PlusIcon,

  TrashIcon,
  DocumentDuplicateIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline';

const TYPE_COLORS = {
  FILAMENT: 'bg-purple-100 text-purple-700',
  MACHINE: 'bg-blue-100 text-blue-700',
  PROCESS: 'bg-green-100 text-green-700',
};

export default function SlicerPresets() {
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [filters, setFilters] = useState({ search: '', type: 'all' });
  const { showToast } = useToast();

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const res = await api.get('/auth/slicer-settings/base');
      setSettings(res.data?.data || []);
    } catch {
      showToast('Failed to load presets', 'error');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch on mount only
  useEffect(() => { fetchSettings(); }, []);

  const filtered = settings
    .filter(s => {
      if (filters.search && !s.name?.toLowerCase().includes(filters.search.toLowerCase())) return false;
      if (filters.type !== 'all' && s.type?.toLowerCase() !== filters.type) return false;
      return true;
    })
    .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));

  const handleSave = async (formData) => {
    const data = new FormData();
    data.append('file', formData.file);
    data.append('type', formData.type);
    if (formData.name) data.append('name', formData.name);
    if (formData.metadata && Object.keys(formData.metadata).length > 0) {
      data.append('metadata', JSON.stringify(formData.metadata));
    }
    if (formData.forceUpload) data.append('forceUpload', 'true');

    await api.post('/auth/slicer-settings/base', data, { headers: { 'Content-Type': 'multipart/form-data' } });
    setShowForm(false);
    fetchSettings();
  };

  const handleDuplicate = async (setting) => {
    try {
      await api.post(`/auth/slicer-settings/base/${setting._id}/duplicate`);
      showToast('Preset duplicated to your workspace', 'success');
      fetchSettings();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to duplicate', 'error');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/auth/slicer-settings/base/${deleteTarget._id}`);
      showToast('Preset deleted', 'success');
      setDeleteTarget(null);
      fetchSettings();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to delete', 'error');
    }
  };


  if (showForm) {
    return <SystemSettingsForm onSave={handleSave} onCancel={() => setShowForm(false)} />;
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Slicer Presets"
        description={`${filtered.length} preset${filtered.length !== 1 ? 's' : ''}`}
        actions={
          <Button icon={PlusIcon} onClick={() => setShowForm(true)}>Upload New</Button>
        }
      />

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search presets..."
            value={filters.search}
            onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
            className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <select
          value={filters.type}
          onChange={(e) => setFilters(f => ({ ...f, type: e.target.value }))}
          className="px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
        >
          <option value="all">All Types</option>
          <option value="filament">Filament</option>
          <option value="machine">Machine</option>
          <option value="process">Process</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-5 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Name</th>
              <th className="px-5 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Type</th>
              <th className="px-5 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Owner</th>
              <th className="px-5 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Updated</th>
              <th className="px-5 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr><td colSpan={5} className="px-5 py-8 text-center text-sm text-slate-500">Loading...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={5} className="px-5 py-8 text-center text-sm text-slate-500">No presets found</td></tr>
            ) : (
              filtered.map((s) => {
                const isSystem = !s.user;
                return (
                  <tr key={s._id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-5 py-3 text-sm font-medium text-slate-900">{s.name}</td>
                    <td className="px-5 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${TYPE_COLORS[s.type] || 'bg-slate-100 text-slate-700'}`}>
                        {s.type}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      {isSystem ? <Badge variant="info" size="sm">System</Badge> : <Badge variant="default" size="sm">Mine</Badge>}
                    </td>
                    <td className="px-5 py-3 text-sm text-slate-500">{s.updatedAt ? new Date(s.updatedAt).toLocaleDateString() : '—'}</td>
                    <td className="px-5 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        <button onClick={() => handleDuplicate(s)} className="p-1.5 text-slate-400 hover:text-blue-600 rounded transition-colors" title="Duplicate">
                          <DocumentDuplicateIcon className="h-4 w-4" />
                        </button>
                        {!isSystem && (
                          <button onClick={() => setDeleteTarget(s)} className="p-1.5 text-slate-400 hover:text-red-600 rounded transition-colors" title="Delete">
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {deleteTarget && (
        <ConfirmationModal
          title="Delete Preset"
          message={`Are you sure you want to delete "${deleteTarget.name}"?`}
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}
